/**
 * The app map, squeezed into text a model can read in front of every question.
 *
 * This is the whole reason the assistant is affordable. The alternative — send
 * the source, or the family's records, or a written manual — costs thousands of
 * tokens a turn and still answers "where do I press?" badly. A structured digest
 * of a few hundred short lines answers it well.
 *
 * Two things keep it small:
 *
 *  - It is built *once* per mode, not per question, and cached. The mode is the
 *    only thing that changes what exists, so there are two possible digests.
 *  - Anything the current mode hides is left out entirely. A Simple-mode
 *    grandparent never spends a token on the globe, and — more importantly —
 *    the model cannot send them to a screen they do not have.
 *
 * The format is lines and pipes rather than JSON: same information, roughly half
 * the tokens, and models read it just as well.
 */
import { isFeatureOn, type ExperienceMode } from '../../app/experience';
import { CONTROLS, HOWTOS, LIMITS, PANELS, RULES, SCREENS } from './appMap';

function inMode(mode: ExperienceMode, feature?: { toString(): string }): boolean {
  if (!feature) return true;
  return isFeatureOn(mode, feature as never);
}

function buildDigest(mode: ExperienceMode, staff: boolean): string {
  const out: string[] = [];

  out.push('## SCREENS  (id | address | what it is)');
  for (const s of SCREENS) {
    if (!inMode(mode, s.feature)) continue;
    if (s.staffOnly && !staff) continue;
    out.push(`${s.id} | ${s.route} | ${s.what}`);
  }

  out.push('');
  out.push('## PANELS  (open over the current screen; they stack and never lose your place)');
  for (const p of PANELS) {
    if (!inMode(mode, p.feature)) continue;
    out.push(`${p.id} | ${p.what}`);
  }

  out.push('');
  out.push('## CONTROLS  (anchor | on which screen | name | what pressing it does)');
  out.push('Use an anchor with the `guide` tool to point at it on the real screen.');
  for (const c of CONTROLS) {
    if (!inMode(mode, c.feature)) continue;
    // Admin controls has a block of its own below: fifteen rows that all begin the same way.
    if (c.on === 'manage') continue;
    out.push(`${c.id} | ${c.on} | ${c.name.en} | ${c.does}`);
  }

  /*
   * Admin controls is a menu of rows, and a row is three things that follow from
   * one word: its anchor (`manage.section.<key>`), its page (`/manage/<key>`) and
   * its name. Saying the pattern once and then only the word costs about half what
   * fifteen full lines would — which is what lets every section be here at all.
   * A member is never told any of it.
   */
  if (staff) {
    const PREFIX = 'manage.section.';
    out.push('');
    out.push(`## ADMIN CONTROLS  (key | name | for) — page /manage/<key>, anchor ${PREFIX}<key>`);
    for (const c of CONTROLS) {
      if (c.on !== 'manage' || !inMode(mode, c.feature)) continue;
      out.push(`${c.id.startsWith(PREFIX) ? c.id.slice(PREFIX.length) : c.id} | ${c.name.en} | ${c.does}`);
    }
  }

  out.push('');
  out.push('## HOW-TOS  (ready-made walks — prefer these; pass the id to `guide`)');
  out.push('A walk marked [handles the mode] turns Advanced on first when it needs to, and skips');
  out.push('that when it is already on. Use it as-is — never tell somebody to switch on a setting.');
  for (const h of HOWTOS) {
    if (!inMode(mode, h.feature)) continue;
    const conditional = h.steps.some((s) => s.onlyIfOff) ? ' [handles the mode]' : '';
    const shown = h.steps.filter((s) => !s.onlyIfOff || !isFeatureOn(mode, s.onlyIfOff));
    out.push(`${h.id} | ${h.title.en}${conditional} | ${shown.map((s) => s.anchor ?? s.route ?? '…').join(' → ')}`);
  }

  out.push('');
  out.push('## RULES  (decisions the family made — these explain most "why did it do that?")');
  for (const r of RULES) out.push(`- ${r.says.en}`);

  out.push('');
  out.push('## LIMITS  (what the app does not do, and whether that is on purpose)');
  out.push('`design` = decided deliberately, not a fault. `prototype` = not built yet, and known.');
  for (const l of LIMITS) out.push(`- [${l.kind}] ${l.says.en}`);

  return out.join('\n');
}

const cache = new Map<string, string>();

/**
 * The digest for one mode. Cached, because it only changes when the mode does.
 */
export function appDigest(mode: ExperienceMode, staff: boolean): string {
  const key = `${mode}:${staff ? 'staff' : 'member'}`;
  let d = cache.get(key);
  if (!d) {
    d = buildDigest(mode, staff);
    cache.set(key, d);
  }
  return d;
}

/** Roughly how many tokens the digest costs — shown in Admin controls so the cost is never a surprise. */
export function approxTokens(text: string): number {
  return Math.round(text.length / 3.7);
}
